import { useNavigate } from 'react-router-dom';
import { Building2, Plus, XCircle } from 'lucide-react';

import { Card } from '../../../components/Shared/Card';
import { Loading } from '../../../components/Shared/Loading';
import { Button } from '../../../components/Shared/Button';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { Badge } from '../../../components/Shared/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Shared/Table';
import { objectTasksKey, selectObjectTasks, useTaskStore } from '../../../store/useTaskStore';
import { useTranslation } from '../../../i18n';
import type { TaskListDto } from '../../../types';

interface ObjectTasksCardProps {
  objectId: string | undefined;
  /** False until the first task fetch for this object has finished. */
  initialLoadComplete: boolean;
  onCreateTask: () => void;
  onRetry: () => void;
}

/** The tasks of an object (moved out of ObjectDetailPage). */
export function ObjectTasksCard({ objectId, initialLoadComplete, onCreateTask, onRetry }: ObjectTasksCardProps) {
  const { t } = useTranslation(['objects', 'common']);
  const navigate = useNavigate();
  const error = useTaskStore((state) => state.error);
  const tasks = useTaskStore(selectObjectTasks(objectId));
  // why: the store's `loading` is shared with other task loads; only this object's own data counts.
  const tasksCached = useTaskStore((state) => !!objectId && objectTasksKey(objectId) in state.objectTasks);

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return t('objects:duration.minutes', { count: mins });
    if (mins === 0) return t('objects:duration.hours', { count: hours });
    return t('objects:duration.hoursMinutes', { hours, minutes: mins });
  };

  return (
    <Card
      title={t('objects:detail.tasksSection')}
      actions={
        <Button onClick={onCreateTask} size="sm" leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}>
          {t('objects:detail.newTask')}
        </Button>
      }
    >
      <p className="text-[12px] text-slate-500 mb-3">{t('objects:detail.tasksDescription')}</p>

      {!initialLoadComplete && !tasksCached ? (
        <Loading />
      ) : error && initialLoadComplete ? (
        <EmptyState
          icon={<XCircle className="w-10 h-10 text-red-500" strokeWidth={1.75} />}
          title={t('common:errors.loadFailed')}
          description={error}
          action={
            <Button variant="secondary" onClick={onRetry}>
              {t('common:actions.retry')}
            </Button>
          }
        />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={<Building2 className="w-12 h-12" strokeWidth={1.75} />}
          title={t('objects:detail.emptyTasks.title')}
          description={t('objects:detail.emptyTasks.description')}
          action={
            <Button onClick={onCreateTask} leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}>
              {t('objects:detail.createTask')}
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>{t('objects:detail.tableTasks.task')}</TableHeader>
              <TableHeader className="hidden md:table-cell">{t('objects:detail.tableTasks.service')}</TableHeader>
              <TableHeader className="hidden sm:table-cell">{t('objects:detail.tableTasks.duration')}</TableHeader>
              <TableHeader className="hidden sm:table-cell">{t('objects:detail.tableTasks.status')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task: TaskListDto) => (
              <TableRow key={task.taskId} onClick={() => navigate(`/tasks/${task.taskId}`)}>
                <TableCell>
                  <div className="font-medium text-[13px] text-slate-900">{task.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 md:hidden">{task.serviceName}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell">{task.serviceName}</TableCell>
                <TableCell className="hidden sm:table-cell tabular-nums">
                  {formatDuration(task.estimatedDurationMinutes)}
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <Badge variant={task.isActive ? 'success' : 'default'}>
                    {task.isActive ? t('common:status.active') : t('common:status.inactive')}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
